import { Tooltip, TooltipContent, TooltipTrigger } from 'cpool-components/general/Tooltip';
import { TooltipAlign, TooltipPosition } from 'cpool-components/general/Tooltip/types';
import { Truncate } from 'cpool-components/general/Truncate';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES } from 'cpool-constants';
import type {
    SchemaLoanRequestResolver_GetSummary,
    SchemaUserResolver_GetAclPermissions,
} from 'cpool-tables/backend-resolvers';
import { isAssignedIKClearingLead } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { useStartIKProcessMutation } from './useStartIKProcessMutation';
import { UserArrowIcon } from '~/components/icons/UserArrowIcon';

type Props = {
    // list rows show only the icon, the label moves into the tooltip
    iconOnly?: boolean;
    lead: Pick<SchemaLoanRequestResolver_GetSummary[number], 'id' | 'nextIKCategory' | 'type' | 'userIds'>;
    permissions?: SchemaUserResolver_GetAclPermissions;
};

export const IKProcessButton: React.FC<Props> = ({ iconOnly = false, lead, permissions }) => {
    const { t } = useTranslation();
    const { nextIKCategory } = lead;
    const { isAwaitingAssignment, isPending, mutate } = useStartIKProcessMutation(lead.id);

    if (!nextIKCategory || !permissions?.commands[AGGREGATE_NAMES.LOAN_REQUEST].startIKProcess) return null;

    const label = t(`LEADS.IK_PROCESS.START.${nextIKCategory}`);
    const isAssignedClearingLead = isAssignedIKClearingLead(lead);
    const button = (
        <Button
            ariaLabel={label}
            className={iconOnly ? undefined : 'w-full whitespace-nowrap'}
            data-testid="ik-process-button"
            disabled={isPending || isAwaitingAssignment || isAssignedClearingLead}
            iconLeft={<UserArrowIcon />}
            onClick={(evt) => {
                evt.preventDefault();
                mutate(nextIKCategory);
            }}
            variant={iconOnly ? ButtonVariant.SECONDARY : ButtonVariant.PRIMARY_OUTLINE}
        >
            {iconOnly ? undefined : <Truncate className="min-w-0" text={label} />}
        </Button>
    );

    return (
        <div onClick={(evt) => evt.stopPropagation()}>
            {isAssignedClearingLead || iconOnly ? (
                <Tooltip>
                    {/* the wrapper is the trigger so the hint also shows while the button is disabled */}
                    <TooltipTrigger asChild>
                        <div>{button}</div>
                    </TooltipTrigger>
                    <TooltipContent align={TooltipAlign.END} side={TooltipPosition.TOP}>
                        {isAssignedClearingLead ? t('LEADS.IK_PROCESS.ALREADY_ASSIGNED_HINT') : label}
                    </TooltipContent>
                </Tooltip>
            ) : (
                button
            )}
        </div>
    );
};
